import React, { useState } from 'react';
import { 
  X, Shield, Clock, CheckCircle, AlertTriangle, 
  Package, User, ArrowRight, Copy, MessageSquare,
  CreditCard, TrendingUp, RefreshCw, AlertCircle
} from 'lucide-react';
import Modal from '../common/Modal';
import { useNavigate } from 'react-router-dom';

const StandaloneEscrowModal = ({ 
  isOpen, 
  onClose, 
  escrow,
  user,
  onConfirmReceipt,
  onRaiseDispute,
  onNavigateToChat,
  onRefresh
}) => {
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!escrow) return null;
  
  // Determine user role
  const isBuyer = user?.user_id === escrow.buyer_id;
  const isSeller = user?.user_id === escrow.seller_id;
  
  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const getStatusConfig = (status) => {
    const configs = {
      'pending': { 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: <Clock className="w-4 h-4" />, 
        text: 'Pending Payment',
        description: 'Waiting for buyer to complete payment'
      },
      'held': { 
        color: 'bg-blue-100 text-blue-800', 
        icon: <Shield className="w-4 h-4" />, 
        text: 'Funds Secured',
        description: 'Payment secured in escrow'
      },
      'released': { 
        color: 'bg-green-100 text-green-800', 
        icon: <CheckCircle className="w-4 h-4" />, 
        text: 'Payment Released',
        description: 'Transaction completed successfully'
      },
      'disputed': { 
        color: 'bg-red-100 text-red-800', 
        icon: <AlertTriangle className="w-4 h-4" />, 
        text: 'Dispute Active',
        description: 'Transaction under review. Fund frozen untill resolved.'
      }
    };
    return configs[status] || configs.pending;
  };
  
  const handleConfirmReceipt = async () => {
    if (!onConfirmReceipt) return;
    
    const confirmed = window.confirm(
      `Confirm you have received the item in good condition?\n\n` +
      `✅ This will release ${formatPrice(escrow.amount)} to the seller.\n` +
      `Transaction will be completed.`
    );
    
    if (!confirmed) return;
    
    try {
      setProcessing(true);
      await onConfirmReceipt();
      onClose();
    } catch (error) {
      // console.error('Error confirming receipt:', error);
      alert('Failed to confirm receipt');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleRaiseDispute = () => {
    if (!onRaiseDispute) return;
    
    onRaiseDispute();
    onClose();
  };
  
  const handleViewDisputeDetails = () => {
    // Navigate to dispute details page
    if (escrow.dispute_id) {
      navigate(`/disputes/${escrow.dispute_id}`);
    }
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      setLoading(true);
      await onRefresh();
      setLoading(false);
    }
  };
  
  const handleCopyId = () => {
    navigator.clipboard.writeText(`ESC-${escrow.escrow_id}`);
    alert('Escrow ID copied to clipboard!');
  };
  
  const handleGoToChat = () => {
    if (onNavigateToChat) {
      onNavigateToChat(escrow);
      onClose();
    }
  };
  
  const statusConfig = getStatusConfig(escrow.status);
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Escrow Transaction"
      size="lg"
      showCloseButton={true}
      showFooter={false}
      customHeader={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-orange-500" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Secure Transaction</h2>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                  {statusConfig.icon}
                  {statusConfig.text}
                </span>
                <span className="text-xs text-gray-500">
                  ID: {escrow.escrow_id}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyId}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900"
              title="Copy Escrow ID"
            >
              <Copy className="w-4 h-4" />
            </button>
            {onRefresh && (
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Amount Card */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 font-medium mb-1">
                Amount in Escrow
              </p>
              <p className="text-3xl font-bold text-blue-900">
                {formatPrice(escrow.amount)}
              </p>
              <p className="text-sm text-blue-600 mt-2">
                {statusConfig.description}
              </p>
            </div>
            <Shield className="w-12 h-12 text-blue-600 opacity-80" />
          </div>
        </div>
        
        {/* Transaction Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Details */}
          <div className="space-y-4">
            {/* Item Info */}
            <div className="bg-white border rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-400" />
                Item Details
              </h4>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Item:</span>
                  <span className="font-medium text-right">{escrow.listing_title || 'Item'}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">{formatDate(escrow.created_at)}</span>
                </div>
                
                {escrow.conversation_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conversation:</span>
                    <span className="font-medium">#{escrow.conversation_id}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Status Timeline */}
            <div className="bg-white border rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                Status Timeline
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Escrow Created</p>
                    <p className="text-xs text-gray-500">{formatDate(escrow.created_at)}</p>
                  </div>
                </div>
                
                {escrow.held_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <Shield className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Funds Secured</p>
                      <p className="text-xs text-gray-500">{formatDate(escrow.held_at)}</p>
                    </div>
                  </div>
                )}
                
                {escrow.status === 'released' && escrow.released_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Payment Released</p>
                      <p className="text-xs text-gray-500">{formatDate(escrow.released_at)}</p>
                    </div>
                  </div>
                )}
                
                {escrow.status === 'disputed' && escrow.dispute_opened_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Dispute Raised</p>
                      <p className="text-xs text-gray-500">{formatDate(escrow.dispute_opened_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Column - Parties */}
          <div className="space-y-4">
            {/* Parties Involved */}
            <div className="bg-white border rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-gray-400" />
                Parties Involved
              </h4>
              
              <div className="space-y-4">
                {/* Buyer */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                      {escrow.buyer_first_name?.charAt(0)?.toUpperCase() || 'B'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {escrow.buyer_first_name ? 
                          `${escrow.buyer_first_name} ${escrow.buyer_last_name || ''}`.trim() : 
                          `Buyer ${escrow.buyer_id}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {isBuyer ? 'You (Buyer)' : 'Buyer'}
                      </p>
                    </div>
                  </div>
                  {isBuyer && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                      You
                    </span>
                  )}
                </div>
                
                {/* Arrow */}
                <div className="flex justify-center">
                  <ArrowRight className="w-5 h-5 text-gray-400 transform rotate-90 md:rotate-0" />
                </div>
                
                {/* Seller */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-bold">
                      {escrow.seller_first_name?.charAt(0)?.toUpperCase() || escrow.seller_store_name?.charAt(0)?.toUpperCase() || 'S'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {escrow.seller_store_name || 
                         (escrow.seller_first_name ? 
                          `${escrow.seller_first_name} ${escrow.seller_last_name || ''}`.trim() : 
                          `Seller ${escrow.seller_id}`)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {isSeller ? 'You (Seller)' : 'Seller'}
                      </p>
                    </div>
                  </div>
                  {isSeller && (
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                      You
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Additional Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">
                    {isBuyer 
                      ? 'As the buyer, you can confirm receipt when you receive the item in good condition.'
                      : 'As the seller, you should ship the item. The buyer will confirm receipt when they receive it.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* ACTION BUTTONS - Only Confirm Payment and Raise Dispute */}
        {/* ACTION BUTTONS */}
<div className="space-y-3">
  {/* Buyer Actions for Held Status */}
  {isBuyer && escrow.status === 'held' && (
    <>
      <button
        onClick={handleConfirmReceipt}
        disabled={processing}
        className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <CheckCircle className="w-5 h-5" />
        {processing ? 'Processing...' : 'Confirm Receipt & Release Payment'}
      </button>
      
      <button
        onClick={handleRaiseDispute}
        disabled={processing}
        className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <AlertTriangle className="w-5 h-5" />
        Raise Dispute
      </button>
    </>
  )}
  
  {/* Seller Actions for Held Status */}
  {isSeller && escrow.status === 'held' && (
    <button
      onClick={handleRaiseDispute}
      disabled={processing}
      className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
    >
      <AlertTriangle className="w-5 h-5" />
      Report Issue / Raise Dispute
    </button>
  )}
  
  {/* For Disputed Status - View Existing Dispute */}
  {escrow.status === 'disputed' && escrow.dispute_id && (
    <button
      onClick={handleViewDisputeDetails}
      className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
    >
      <AlertTriangle className="w-5 h-5" />
      View Dispute Details
    </button>
  )}
  
  {/* For Released Status - Show Completion Message */}
  {escrow.status === 'released' && (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
      <p className="font-medium text-green-800">Transaction Completed</p>
      <p className="text-sm text-green-700">
        Payment has been released {isBuyer ? 'to the seller' : 'to your account'}
      </p>
    </div>
  )}
  
  {/* For Pending Status - Buyer Needs to Pay */}
  {escrow.status === 'pending' && isBuyer && (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-yellow-800">Awaiting Payment</p>
          <p className="text-sm text-yellow-700 mt-1">
            Complete payment to secure funds in escrow. Go to the conversation to pay.
          </p>
        </div>
      </div>
    </div>
  )}
  
  {/* Go to Chat Button (Always show if has conversation) */}
  {/* {escrow.conversation_id && (
    <button
      onClick={handleGoToChat}
      className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
    >
      <MessageSquare className="w-5 h-5" />
      Go to Conversation
    </button>
  )} */}
</div>
      </div>
    </Modal>
  );
};

export default StandaloneEscrowModal;